/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
import { css, jsx } from '@compiled/react';

import Button from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives/compiled';
import AiGenerativeRemoveIcon from '@atlaskit/icon-lab/core/ai-generative-remove';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';

import { LazyAnimatedRovoIcon } from '@atlassian/conversation-assistant-ui-components';

import { usePageContentId } from '@confluence/page-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';

import { useKeepPageStructure } from './hooks/useKeepPageStructure.ts';

const i18n = defineMessages({
	keepPageStructure: {
		id: 'experiment-copy-with-rovo.button.label',
		defaultMessage: 'Remove content, keep structure',
		description:
			'Button label to strip user-specific content while preserving page structure using Rovo AI',
	},
	loading: {
		id: 'experiment-copy-with-rovo.button.loading',
		defaultMessage: 'Improving content...',
		description: 'Button label shown while the keep page structure request is being processed',
	},
	error: {
		id: 'experiment-copy-with-rovo.button.error',
		defaultMessage: 'Retry',
		description: 'Button label shown when the keep page structure request fails',
	},
	success: {
		id: 'experiment-copy-with-rovo.button.success',
		defaultMessage: 'Content improved',
		description: 'Button label shown when the page content was successfully improved',
	},
	rovoLoadingAlt: {
		id: 'experiment-copy-with-rovo.rovo-loading.alt',
		defaultMessage: 'Rovo is processing',
		description: 'Alt text for the Rovo loading animation',
	},
});

const buttonContainerStyle = css({
	paddingTop: token('space.100'),
	paddingBottom: token('space.050'),
});

const loadingIconStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '16px',
	height: '16px',
});

type KeepPageStructureIconProps = {
	isLoading: boolean;
	isSuccess: boolean;
	error: string | null;
	loadingAlt: string;
};

const KeepPageStructureIcon = ({
	isLoading,
	isSuccess,
	error,
	loadingAlt,
}: KeepPageStructureIconProps) => {
	if (isLoading) {
		return (
			<span css={loadingIconStyle}>
				<LazyAnimatedRovoIcon alt={loadingAlt} width="16px" height="16px" />
			</span>
		);
	}
	if (error) {
		return <CrossCircleIcon label="" color={token('color.icon.danger')} />;
	}
	if (isSuccess) {
		return <StatusSuccessIcon label="" color={token('color.icon.success')} />;
	}
	return <AiGenerativeRemoveIcon label="" />;
};

export type KeepPageStructureButtonProps = {
	/**
	 * The content ID of the page. Falls back to page context if not provided.
	 */
	contentId?: string;
};

export const KeepPageStructureButton = ({
	contentId: contentIdProp,
}: KeepPageStructureButtonProps) => {
	const { formatMessage } = useIntl();
	const { isRovoEnabled } = useSessionData();
	const pageMode = useGetPageMode();

	const [pageContentId] = usePageContentId();
	const contentId = pageContentId || contentIdProp;

	const { keepPageStructure, isLoading, error, isSuccess, reset } = useKeepPageStructure({
		contentId,
		enabled: isRovoEnabled && fg('aifc_create_enabled'),
	});

	const handleClick = useCallback(() => {
		if (!fg('aifc_create_enabled')) {
			return;
		}

		if (isLoading) {
			reset();
			return;
		}

		if (isSuccess) {
			// Already applied; reset to allow re-running
			reset();
			return;
		}

		keepPageStructure();
	}, [isLoading, isSuccess, reset, keepPageStructure]);

	const loadingAlt = formatMessage(i18n.rovoLoadingAlt);
	const renderIcon = useCallback(
		() => (
			<KeepPageStructureIcon
				isLoading={isLoading}
				isSuccess={isSuccess}
				error={error}
				loadingAlt={loadingAlt}
			/>
		),
		[isLoading, isSuccess, error, loadingAlt],
	);

	const isAllowedPageMode = pageMode === PageMode.LIVE || pageMode === PageMode.EDIT;

	if (!isAllowedPageMode || !isRovoEnabled || !fg('aifc_create_enabled')) {
		return null;
	}

	const getButtonLabel = () => {
		if (error) {
			return (
				<Text as="span" color="color.text.danger">
					{formatMessage(i18n.error)}
				</Text>
			);
		}
		if (isLoading) {
			return formatMessage(i18n.loading);
		}
		if (isSuccess) {
			return formatMessage(i18n.success);
		}
		return formatMessage(i18n.keepPageStructure);
	};

	return (
		<div css={buttonContainerStyle} aria-live="polite">
			<Button
				appearance="subtle"
				iconBefore={renderIcon}
				onClick={handleClick}
				testId="experiment-copy-with-rovo-button"
			>
				{getButtonLabel()}
			</Button>
		</div>
	);
};
